import ParameterValidator from '@mvps-genesis/parameter-validator';
import InputPassword from './InputPassword';
import InputToggle from './InputToggle';

/*
 *--------------------------------------------------------------------------
 * HTML Form Validator
 *--------------------------------------------------------------------------
 *
 * Class to add inline validation to HTML forms.
 *
 */
export default class HTMLFormValidator {

	/**
	 * Parameter Validator object
	 *
	 * @var	@mvps-genesis/parameter-validator
	 */
	#validator;

	/**
	 * Class options object. See class constructor() for option definitions.
	 *
	 * @var Object
	 * @property	Function	beforeSubmit				Reference to a function to be called after form validation and before form submission.
	 * @property	String		errorClass					Class to apply to error message elements. Defaults to 'g-form-field__error'.
	 * @property	String		errorHideClass				Class to hide error message elements. Defaults to 'u-hidden'.
	 * @property	String		errorInsertPosition			Position to insert error messages relative to the field container element. Value must be one of the following: 'beforebegin', 'afterbegin', 'beforeend', or 'afterend'. Defaults to 'beforeend'.
	 * @property	Object		errorMessages				Override any or all of the form field error messages per tag name and type. Mapping defaults to the following:
	 *															DEFAULT: 'Please enter a value',
	 * 															INPUT_CHECKBOX: 'Please check the checkbox',
	 * 															INPUT_DATE: 'Please enter a valid date',
	 * 															INPUT_EMAIL: 'Please enter a valid email',
	 * 															INPUT_FILE: 'Please select a valid file',
	 * 															INPUT_NUMBER: 'Please enter a valid number',
	 * 															INPUT_PASSWORD: 'Please enter a valid password',
	 * 															INPUT_RADIO: 'Please select an option',
	 * 															INPUT_RANGE: 'Please select a range',
	 * 															INPUT_SEARCH: 'Please enter a value',
	 * 															INPUT_TEL: 'Please enter a valid telephone number',
	 * 															INPUT_TEXT: 'Please enter a value',
	 * 															INPUT_URL: 'Please enter a valid URL',
	 * 															SELECT: 'Please select an option',
	 * 															TEXTAREA: 'Please enter a value'
	 * @property	String		fieldContainer				The container element for form fields. Defaults to ''. If default, targets the field parent element.
	 * @property	String		fieldContainerErrorClass	Class to apply to form fields container element on validation errors. Defaults to 'has-error'.
	 * @property	Element		formElement					The form element being validated, must be a DOM element.
	 * @property	String		labelRequiredSelector		Selector to indicate label has required form field. Defaults to '.is-required'.
	 * @property	Function	onValidate					Reference to a function to be called during form validation, MUST return boolean.
	 * @property	Boolean		scrollToFirstError			Scroll to first error after form validation runs. Defaults to true.
	 * @property	Object		scrollToOptions				scrollIntoViewOptions object - https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView. Defaults to {behavior: 'smooth', block: 'center'}.
	 */
	options;

	/**
	 * The form element being validated.
	 *
	 * @var Element
	 */
	formElement;

	/**
	 * List of form elements to validate
	 *
	 * @var NodeList
	 */
	validateElements;

	/**
	 * Map of form field error messages
	 *
	 * @var Object
	 */
	errorMessages;

	/**
	 * Map of HTML data attributes
	 *
	 * @var Object
	 */
	attributes;

	/**
	 * InputPassword class contains logic for password toggle
	 * and requirement display feedback
	 *
	 * @var Class
	 */
	InputPassword;

	/**
	 * InputToggle class contains logic for toggling the display
	 * of secondary input fields
	 *
	 * @var Class
	 */
	InputToggle;

	/**
	 * Create a new HTMLFormValidator object
	 *
	 * @property	Object	options	See options variable definition above for properties documentation.
	 */
	constructor ({
		beforeSubmit = null,
		errorClass = 'g-form-field__error',
		errorHideClass = 'u-hidden',
		errorInsertPosition = 'beforeend',
		errorMessages = {},
		fieldContainer = '',
		fieldContainerErrorClass = 'has-error',
		formElement = '.js-form-validator',
		labelRequiredSelector = '.is-required',
		onValidate = null,
		scrollToFirstError = true,
		scrollToOptions = {
			behavior: 'smooth',
			block: 'center'
		}
	} = {}) {
		this.#validator = new ParameterValidator(this);

		this.formElement = this.#validator.isElement('formElement', formElement);

		if (this.formElement.tagName !== 'FORM') {
			throw new TypeError(`${this.constructor.name}::A valid form element is required`);
		}

		this.errorMessages = this.#validator.isObject('errorMessages', errorMessages);

		this.options = {
			beforeSubmit: beforeSubmit,
			errorClass: this.#validator.isString('errorClass', errorClass),
			errorHideClass: this.#validator.isString('errorHideClass', errorHideClass),
			errorInsertPosition: this.#validator.inSet('errorInsertPosition', this.#validator.isString('errorInsertPosition', errorInsertPosition), [
				'beforebegin',
				'afterbegin',
				'beforeend',
				'afterend'
			]),
			fieldContainer: this.#validator.isString('fieldContainer', fieldContainer),
			fieldContainerErrorClass: this.#validator.isString('fieldContainerErrorClass', fieldContainerErrorClass),
			labelRequiredSelector: this.#validator.isString('labelRequiredSelector', labelRequiredSelector),
			onValidate: onValidate,
			scrollToFirstError: this.#validator.isBoolean('scrollToFirstError', scrollToFirstError),
			scrollToOptions: this.#validator.isObject('scrollToOptions', scrollToOptions)
		};

		this.init();
	}

	init () {
		this.setInputPasswordToggle();
		this.setInputToggle();
		this.setAttributes();
		this.setValidateElements();
		this.setErrorMessages();
		this.disableBrowserValidation();
		this.bindEvents();

		if (this.formElement.hasAttribute(this.attributes.form.toggleFields)) {
			this.initSecondaryToggleFields();
		}
	};

	setAttributes () {
		this.attributes = {
			error: {
				disable: {
					blur: 'data-error-disable-blur'
				},
				message: 'data-error-message'
			},
			form: {
				toggleFields: 'data-form-toggle-fields'
			}
		};
	}

	setInputPasswordToggle () {
		const passwordElements = this.#validator.isElementList('InputPassword', this.formElement.querySelectorAll('input[type="password"]'));

		this.InputPassword = [];

		for (const element of passwordElements) {
			this.InputPassword.push(new InputPassword(element));
		}
	}

	setInputToggle () {
		const toggleElements = this.#validator.isElementList('InputToggle', this.formElement.getElementsByClassName('js-input-toggle'));

		for (const element of toggleElements) {
			new InputToggle(element);
		}
	}

	setValidateElements () {
		this.validateElements = this.#validator.isElementList('validateElements', '[required], [pattern]', {container: this.formElement});
	}

	setErrorMessages () {
		this.errorMessages.DEFAULT = this.#validator.isString('errorMessages.DEFAULT', this.errorMessages.DEFAULT || 'Please enter a value');
		this.errorMessages.INPUT_CHECKBOX = this.#validator.isString('errorMessages.INPUT_CHECKBOX', this.errorMessages.INPUT_CHECKBOX || 'Please check the checkbox');
		this.errorMessages.INPUT_DATE = this.#validator.isString('errorMessages.INPUT_DATE', this.errorMessages.INPUT_DATE || 'Please enter a valid date');
		this.errorMessages.INPUT_EMAIL = this.#validator.isString('errorMessages.INPUT_EMAIL', this.errorMessages.INPUT_EMAIL || 'Please enter a valid email');
		this.errorMessages.INPUT_FILE = this.#validator.isString('errorMessages.INPUT_FILE', this.errorMessages.INPUT_FILE || 'Please select a valid file');
		this.errorMessages.INPUT_NUMBER = this.#validator.isString('errorMessages.INPUT_NUMBER', this.errorMessages.INPUT_NUMBER || 'Please enter a valid number');
		this.errorMessages.INPUT_PASSWORD = this.#validator.isString('errorMessages.INPUT_PASSWORD', this.errorMessages.INPUT_PASSWORD || 'Please enter a valid password');
		this.errorMessages.INPUT_RADIO = this.#validator.isString('errorMessages.INPUT_RADIO', this.errorMessages.INPUT_RADIO || 'Please select an option');
		this.errorMessages.INPUT_RANGE = this.#validator.isString('errorMessages.INPUT_RANGE', this.errorMessages.INPUT_RANGE || 'Please select a range');
		this.errorMessages.INPUT_SEARCH = this.#validator.isString('errorMessages.INPUT_SEARCH', this.errorMessages.INPUT_SEARCH || 'Please enter a value');
		this.errorMessages.INPUT_TEL = this.#validator.isString('errorMessages.INPUT_TEL', this.errorMessages.INPUT_TEL || 'Please enter a valid telephone number');
		this.errorMessages.INPUT_TEXT = this.#validator.isString('errorMessages.INPUT_TEXT', this.errorMessages.INPUT_TEXT || 'Please enter a value');
		this.errorMessages.INPUT_URL = this.#validator.isString('errorMessages.INPUT_URL', this.errorMessages.INPUT_URL || 'Please enter a valid URL');
		this.errorMessages.SELECT = this.#validator.isString('errorMessages.SELECT', this.errorMessages.SELECT || 'Please select an option');
		this.errorMessages.TEXTAREA = this.#validator.isString('errorMessages.TEXTAREA', this.errorMessages.TEXTAREA || 'Please enter a value');
	}

	disableBrowserValidation () {
		if (!this.formElement.hasAttribute('novalidate')) {
			this.formElement.setAttribute('novalidate', true);
		}
	};

	bindEvents () {
		this.addValidateElementsEvents();
		this.addSubmitEvents();
	}

	addValidateElementsEvents () {
		for (const element of this.validateElements) {
			this.addValidateElementListeners(element);
		}
	}

	removeValidateElementsEvents () {
		for (const element of this.validateElements) {
			this.removeValidateElementListeners(element);
		}
	}

	/**
	 * Function for adding an event listener for inline validation on a single form field
	 *
	 * @param {HTMLElement} element - The form field being validated, must be a DOM element
	 */
	addValidateElementListeners (formElement) {
		const element = this.#validator.isElement('addValidateElementListeners - formElement', formElement);

		element.validateEventListener = this.validateFormElement.bind(this, element);

		if (element.type === 'radio' || element.type === 'checkbox') {
			element.addEventListener('change', element.validateEventListener);
		} else {
			element.addEventListener('input', element.validateEventListener);

			if (!element.hasAttribute(this.attributes.error.disable.blur)) {
				element.addEventListener('blur', element.validateEventListener);
			}
		}
	}

	removeValidateElementListeners (formElement) {
		const element = this.#validator.isElement('removeValidateElementListeners - formElement', formElement);

		if (typeof element.validateEventListener !== 'function') {
			return;
		}

		if (element.type === 'radio' || element.type === 'checkbox' || element.type === 'input') {
			element.removeEventListener('change', element.validateEventListener);
		} else {
			element.removeEventListener('input', element.validateEventListener);
			element.removeEventListener('blur', element.validateEventListener);
		}
	}

	addSubmitEvents () {
		const submitElement = this.#validator.isElement('addSubmitEvents - submitElement', '[type="submit"]', {container: this.formElement});

		submitElement.addEventListener('click', (event) => {
			this.validateForm(event);
		});
	}

	/**
	 * Function for validating a single form field
	 *
	 * @param {HTMLElement} element - The form field being validated, must be a DOM element
	 * @param {Event} [event] An optional event emitted from the form field
	 * @returns {boolean} Returns true if element is hidden or passes validation, returns false if element fails validation
	 */
	validateFormElement (formElement, event) {
		const element = this.#validator.isElement('validateFormElement - formElement', formElement);

		// Only validate if element is visible
		if (element.offsetParent === null) {
			return true;
		}

		// Show or hide error based on validity
		if (element.validity.valid) {
			this.toggleError(element, 'hide');

			return true;
		}

		// If theres an element event, then only show the error when it has blurred
		if (!event || event.type === 'blur') {
			this.toggleError(element, 'show');
		}

		return false;
	};

	/**
	 * Function for validating all required fields of a form, one field at a time.
	 * Calls any custom validation script referenced during instantiation before standard validation
	 * Calls any custom pre-submission script referenced during instantiation after successful validation
	 * Submits the form if all validation is successful
	 */
	validateForm (event) {
		let runBeforeSubmit = true;

		// Run custom validation if defined
		if (typeof this.options.onValidate === 'function') {
			runBeforeSubmit = this.options.onValidate(this, event);
		}

		// Validate each form element
		for (const element of this.validateElements) {
			if (!this.validateFormElement(element) && runBeforeSubmit) {
				runBeforeSubmit = false;
			}
		}

		// Do not submit form if validation is unsuccessful
		if (!runBeforeSubmit) {
			event.preventDefault();

			if (this.options.scrollToFirstError) {
				this.scrollToFirstError();
			}

			return;
		}

		// Run before submit function is defined
		if (typeof this.options.beforeSubmit === 'function') {
			this.options.beforeSubmit(this, event);
		}
	};

	/**
	 * Function for displaying or hiding an error message for a validated form field
	 *
	 * @param {HTMLElement} element - The form field being validated, must be a DOM element
	 * @param {'show'|'hide'} action - Show or hide the error message
	 */
	toggleError (formElement, action) {
		const element = this.#validator.isElement('toggleError - formElement', formElement);
		const isInputPassword = this.InputPassword.find(instance => instance.element === element);

		// Validate arguments
		if (action !== 'show' && action !== 'hide') {
			throw new TypeError(`${this.constructor.name}::toggleError an action of 'show' or 'hide'`);
		}

		let errorContainer = element.parentElement;

		if (this.options.fieldContainer) {
			errorContainer = element.closest(this.options.fieldContainer);
		}

		if (!errorContainer) {
			throw new TypeError(`${this.constructor.name}::toggleError requires a valid container element`);
		}

		const errorElement = this.getErrorElement(errorContainer);

		// Toggle appropriate classes and error messages
		if (action === 'show') {
			errorContainer.classList.add(this.options.fieldContainerErrorClass);

			// Do not display error message for the `InputPassword` element
			if (isInputPassword) {
				return;
			}

			if (errorElement) {
				errorElement.classList.remove(this.options.errorHideClass);
			} else {
				// Create an error message if one does not exist yet
				errorContainer.insertAdjacentElement(this.options.errorInsertPosition, this.createErrorMessage(element));
			}
		} else {
			errorContainer.classList.remove(this.options.fieldContainerErrorClass);

			// Do not display error message for the `InputPassword` element
			if (isInputPassword) {
				return;
			}

			if (errorElement) {
				errorElement.classList.add(this.options.errorHideClass);
			}
		}
	};

	getErrorElement (errorContainerElement) {
		const containerElement = this.#validator.isElement('getErrorElement - errorContainerElement', errorContainerElement);

		if (this.options.errorInsertPosition === 'beforebegin') {
			const previousSibling = containerElement.previousElementSibling;

			if (previousSibling && previousSibling.classList.contains(this.options.errorClass)) {
				return previousSibling;
			}

			return false;
		} else if (this.options.errorInsertPosition === 'afterend') {
			const nextSibling = containerElement.nextElementSibling;

			if (nextSibling && nextSibling.classList.contains(this.options.errorClass)) {
				return nextSibling;
			}

			return false;
		}

		const errorElement = containerElement.getElementsByClassName(this.options.errorClass)[0];

		return errorElement ? errorElement : false;
	}

	/**
	 * DOM element creation function for generating an error message
	 *
	 * @param {HTMLElement} element - The form field being validated, must be a DOM element
	 * @param {String} message - Optional error message. If not provided, logic will run to build out the error message
	 * @returns {HTMLElement} A new DOM element containing an error message, to be placed below the form field
	 */
	createErrorMessage (formElement, message = '') {
		const element = this.#validator.isElement('createErrorMessage - formElement', formElement);
		let errorMessage = this.#validator.isString('createErrorMessage - message', message);
		let errorElement = document.createElement('div');

		// Build the error message if one was not passed in
		if (!errorMessage.length) {
			if (element.hasAttribute(this.attributes.error.message)) {
				// Custom error message
				errorMessage = element.getAttribute(this.attributes.error.message);
			} else if (element.hasAttribute('title')) {
				// Custom error message from title
				errorMessage = element.getAttribute('title');
			} else {
				// Canned message based on type of element
				let elementType = element.tagName;

				if (elementType === 'INPUT') {
					elementType += '_' + element.getAttribute('type').toUpperCase();
				}

				errorMessage = this.errorMessages[elementType] || this.errorMessages.DEFAULT;

				// Add min and max values to errorMessage
				if (elementType === 'INPUT_NUMBER') {
					const min = element.getAttribute('min');
					const max = element.getAttribute('max');

					if (min) {
						errorMessage += `, minimum: ${min}`;
					}

					if (max) {
						errorMessage += `, maximum: ${max}`;
					}
				}
			}
		}

		// Add class and message to error element
		errorElement.classList.add(this.options.errorClass);
		errorElement.textContent = errorMessage;

		return errorElement;
	};

	scrollToFirstError () {
		const firstErrorElement = this.formElement.getElementsByClassName(this.options.fieldContainerErrorClass)[0];

		if (!firstErrorElement) {
			return;
		}

		// Check if error element is currently in view and return if true
		const elementBoundingClientRect = firstErrorElement.getBoundingClientRect();

		if (elementBoundingClientRect.top > 0 && elementBoundingClientRect.bottom < window.innerHeight) {
			return;
		}

		firstErrorElement.scrollIntoView(this.options.scrollToOptions);
	}

	initSecondaryToggleFields () {
		const observer = new MutationObserver((mutations, me) => {
			if (typeof window.ToggleDetails === 'object') {
				me.disconnect();

				if (!window.ToggleDetails.trigger || !window.ToggleDetails.secondary_fields) {
					return;
				}

				window.ToggleDetails.trigger.addEventListener('click', () => {
					this.setSecondaryToggleFields();
				});

				this.setSecondaryToggleFields();
			}
		});

		observer.observe(document, {
			childList: true,
			subtree: true
		});
	}

	// eslint-disable-next-line max-lines-per-function
	setSecondaryToggleFields () {
		const triggerElement = this.#validator.isElement('setSecondaryToggleFields - triggerElement', window.ToggleDetails.trigger);
		const secondaryFields = this.#validator.isElement('setSecondaryToggleFields - secondaryFields', window.ToggleDetails.secondary_fields);
		const requiredLabelElements = secondaryFields.querySelectorAll(this.options.labelRequiredSelector);

		if (!requiredLabelElements) {
			return;
		}

		let requiredInputElements = [];

		for (const element of requiredLabelElements) {
			let fieldContainer = element.parentElement;

			if (this.options.fieldContainer) {
				if (element.matches(this.options.fieldContainer)) {
					fieldContainer = element;
				} else {
					fieldContainer = element.closest(this.options.fieldContainer);
				}
			}

			if (!fieldContainer) {
				return;
			}

			const inputFields = fieldContainer.querySelectorAll('input, select');

			if (!inputFields) {
				return;
			}

			for (const inputField of inputFields) {
				requiredInputElements.push(inputField);
			}
		}

		if (triggerElement.checked) {
			for (const inputElement of requiredInputElements) {
				inputElement.required = false;
				this.removeValidateElementListeners(inputElement);
			}
		}
		else {
			for (const inputElement of requiredInputElements) {
				inputElement.required = true;
				this.addValidateElementListeners(inputElement);
			}
		}

		this.setValidateElements();
	}

}
